import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import GlassCard from '../Ui/GlassCard';
import { useAuth } from '@/api/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { PasswordInput } from '../Ui/PasswordInput';
import { useRef } from 'react';
import StagingRoom from './StagingRoom';
import RoomSelector from './RoomSelector';
import { makeTrace } from '@/utils/operationalTrace';
import rtClient, { subscribeOperationChange, verifyOperationConsistency } from '@/api/realtimeClient';
import { playProceduralSound } from '../System/SoundManager';

function requestPostAuthSplash(target) {
    window.__openPostAuthSplash = { target };
}

// function MockValidate(values) { // Removido conforme política WYSIWYG
//     // simple mock validation to integrate with existing AuthContext
//     if (!values.username) return { ok: false, message: 'Usuário obrigatório' };
//     return { ok: true };
// }

// returnTo (FIELD-P3): caminho interno ja validado pela lista fechada de
// src/features/field/fieldReturnIntent.js. Presente so quando o acesso
// comecou no REACTA FIELD; ausente, o fluxo do desktop segue igual.
// credentialToken (SEC-INV-01): token do link de ativacao/recuperacao
// (#credencial=...), lido pelo Portal. Com ele o portal abre direto no modo
// "Definir senha".
export default function AccessGateway({ isOpen, onClose, onAuthSuccess, initialMode = 'operator', returnTo = null, credentialToken = null }) {
    const { user, checkUserAuth } = useAuth();
    const [visible, setVisible] = useState(isOpen);
    const [form, setForm] = useState({ operatorId: '', accessKey: '', tenant: '', guestCode: '', missionToken: '', trainingKey: '' });
    const [error, setError] = useState(null);
    const [mode, setMode] = useState(initialMode);
    const [loadingStage, setLoadingStage] = useState(null);
    const [statusAnim, setStatusAnim] = useState(0);
    const [activeOperationId, setActiveOperationId] = useState(() => rtClient.getActiveOperationId() || null);
    const [recoveryOpen, setRecoveryOpen] = useState(false);
    const [recoveryEmail, setRecoveryEmail] = useState('');
    const [recoveryCode, setRecoveryCode] = useState('');
    const [recoveryStatus, setRecoveryStatus] = useState(null);
    const [recoveryPending, setRecoveryPending] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
    const [credentialStatus, setCredentialStatus] = useState(null);
    const [credentialPending, setCredentialPending] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const statusInterval = useRef(null);

    useEffect(() => setVisible(isOpen), [isOpen]);
    useEffect(() => {
        if (isOpen) setMode(credentialToken ? 'credential' : initialMode);
    }, [isOpen, initialMode, credentialToken]);

    // trace session start when gateway opens
    useEffect(() => {
        if (visible) {
            try { makeTrace({ eventType: 'SESSION_START', sourceModule: 'AccessGateway' }); } catch (e) { }
            // Realtime connection will be established AFTER successful login to ensure correct role
        }
    }, [visible]);

    const handleChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

    useEffect(() => {
        // subtle status pulse for footer
        statusInterval.current = setInterval(() => setStatusAnim((s) => (s + 1) % 1000), 1200);
        return () => clearInterval(statusInterval.current);
    }, []);

    const simulateSequence = async (sequence = []) => {
        for (const step of sequence) {
            setLoadingStage(step.label);
            // small delay to feel cinematic
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, step.delay || 800));
        }
    };

    const handleSubmit = async (e) => {
        e && e.preventDefault();
        setError(null);
        if (mode === 'credential') return;
        if (mode === 'guest') {
            // SEC-INV-01: todo convite exige codigo + senha individual.
            if (!form.guestCode) { playProceduralSound('error'); return setError('Informe o código do convite.'); }
            if (!form.missionToken) { playProceduralSound('error'); return setError('Informe a senha do convite.'); }
        }
        // mode-aware minimal validation
        if (mode === 'operator') {
            if (!form.operatorId) { playProceduralSound('error'); return setError('Nome de usuario obrigatorio'); }
            if (!form.accessKey) { playProceduralSound('error'); return setError('Chave de acesso obrigatoria'); }
        } else if (mode === 'training') {
            if (!form.trainingKey) { playProceduralSound('error'); return setError('Training Access Key obrigatório'); }
        }

        // show cinematic steps
        if (mode === 'operator') {
            await simulateSequence([
                { label: 'AUTHENTICATING...', delay: 900 },
                { label: 'VERIFYING CREDENTIALS...', delay: 1000 },
                { label: 'INITIALIZING CORE INTERFACE...', delay: 900 }
            ]);
        } else if (mode === 'guest') {
            await simulateSequence([
                { label: 'VERIFYING SESSION...', delay: 900 },
                { label: 'CHECKING AUTHORIZATION...', delay: 900 }
            ]);
        } else {
            await simulateSequence([
                { label: 'VALIDATING TRAINING KEY...', delay: 900 },
                { label: 'LOADING SANDBOX...', delay: 900 }
            ]);
        }

        // Try to persist session via backend, fallback to in-memory mock if server unavailable
        let backendOk = false;
        let inviteRoomId = null; // Track room from invite code
        let authenticatedUserPayload = null;
        let authenticatedSessionPayload = null;
        try {
            const username = mode === 'guest'
                ? form.guestCode
                : form.operatorId || form.trainingKey || 'guest';
            const password = mode === 'guest'
                ? form.missionToken
                : form.accessKey || form.trainingKey || '';

            let resp;
            if (mode === 'guest') {
                // SEC-INV-01: ingresso unico de convite (operacional ou
                // institucional) — o servidor decide a modalidade pelo codigo.
                resp = await fetch('/api/guest/join', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code: form.guestCode.trim(), password }) });
            } else {
                // Afinidade de slot funcional (ver sessionService.
                // createNumberedFunctionalSession, MANIFESTO-REACTAORION-35
                // Fase 7): manda de volta o operatorId numerado (ex.:
                // "webmaster-02") que este navegador recebeu da ultima vez,
                // para o backend tentar reaproveitar o MESMO slot em vez de
                // sempre alocar um novo a cada login nativo repetido.
                let preferredOperatorId = '';
                try { preferredOperatorId = localStorage.getItem('reacta_functionalOperatorId') || ''; } catch (e) { }
                resp = await fetch('/api/auth/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password, inviteCode: '', preferredOperatorId }) });
            }
            if (resp.ok) {
                const payload = await resp.json();
                if (payload && payload.ok) {
                    const s = payload.session || {};
                    const loginUser = payload.user || s || { name: username };
                    // normalize session id (support multiple backend formats)
                    const sid = s.sessionId || s.session_id || s.session || null;
                    if (sid) {
                        localStorage.setItem('reacta_sessionId', sid);
                        sessionStorage.setItem('reacta_sessionId', sid);
                        // Remove legacy key format to avoid mismatches
                        localStorage.removeItem('reacta_session_id');
                        sessionStorage.removeItem('reacta_session_id');
                    }
                    // persist token if provided
                    if (payload.token) localStorage.setItem('orionToken', payload.token);

                    // Extract room from invite if present
                    if (payload.inviteRoom) {
                        inviteRoomId = payload.inviteRoom.operationId;
                        rtClient.setOperationId(inviteRoomId);
                        localStorage.setItem('reacta_roomName', payload.inviteRoom.name || 'Sala Convidado');
                        localStorage.setItem('reacta_inviteMode', 'true');
                    } else if (payload.invited) {
                        setLoadingStage(null);
                        return setError('Convite autenticado, mas sem sala operacional vinculada. Gere um novo convite selecionando a sala correta.');
                    }

                    // Extract role from multiple possible locations - prioritize backend response
                    let userRole = loginUser.role || loginUser.role_name || loginUser.roleName || payload.role || loginUser.userRole;
                    
                    // FALLBACK CRÍTICO: Se backend não retornou role, não inferir em produção
                    if (!userRole || userRole === 'Observer') {
                        userRole = null; // Não gerar roles fictícios em produção
                        console.warn('[AccessGateway] Backend não retornou role, ou retornou Observer. Role definido como null conforme política WYSIWYG.');
                    }

                    // REGRA DE NEGÓCIO: Roles privilegiadas são funcionários REACTA/instituição (sem nome pessoal no login)
                    const privilegedRoles = ['institutional_owner', 'webmaster', 'super_admin', 'simulation_manager'];
                    const normalizedRole = String(userRole || '').toLowerCase();
                    const isPrivilegedRole = privilegedRoles.includes(normalizedRole);
                    
                    let userName;
                    if (isPrivilegedRole) {
                        // Roles privilegiadas: usar título da função (não nome de pessoa)
                        const roleTitles = {
                            'institutional_owner': 'Institutional Owner',
                            'webmaster': 'Webmaster',
                            'super_admin': 'Super Admin',
                            'simulation_manager': 'Gestor da Operação'
                        };
                        userName = roleTitles[normalizedRole] || userRole;
                    } else {
                        // Usuários comuns/convidados: usar nome de pessoa do backend
                        userName = loginUser.name || loginUser.operatorId || loginUser.operator_id || loginUser.username || username || 'Operador';
                    }
                    const normalizedOperatorId = loginUser.operatorId
                        || loginUser.operator_id
                        || loginUser.id
                        || loginUser.email
                        || username
                        || userName;
                    authenticatedUserPayload = {
                        ...loginUser,
                        name: userName,
                        full_name: loginUser.full_name || userName,
                        operatorId: normalizedOperatorId,
                        operator_id: loginUser.operator_id || normalizedOperatorId,
                        role: userRole,
                        roleName: loginUser.roleName || loginUser.role_name || userRole,
                        tenant: loginUser.tenant || loginUser.institution || form.tenant || 'default',
                        isPrivileged: isPrivilegedRole
                    };
                    // Lembra o slot funcional numerado recebido (ex.:
                    // "webmaster-02") para o proximo login deste navegador
                    // tentar reclama-lo de volta — ver preferredOperatorId
                    // acima e MANIFESTO-REACTAORION-35 Fase 7.
                    if (/^webmaster(-\d+)?$/.test(String(normalizedOperatorId || ''))) {
                        try { localStorage.setItem('reacta_functionalOperatorId', normalizedOperatorId); } catch (e) { }
                    }
                    authenticatedSessionPayload = s;

                    // A fonte da verdade agora é o backend. Apenas salvamos o ID da sessão
                    // e disparamos a revalidação do AuthContext.
                    if (inviteRoomId) {
                        rtClient.setOperationId(inviteRoomId);
                    }
                    
                    backendOk = true;
                }
            } else if ([400, 401, 403, 429].includes(resp.status)) {
                const payload = await resp.json().catch(() => ({}));
                setLoadingStage(null);
                playProceduralSound('error');
                return setError(payload.details || 'Credenciais inválidas.');
            }
        } catch (e) {
            console.warn('[AccessGateway] backend login failed', e?.message || e);
        }

        if (!backendOk) {
            setLoadingStage(null);
            playProceduralSound('error');
            return setError('Não foi possível autenticar. Verifique as credenciais e tente novamente.');
        }

        // FIELD-P3: login iniciado no FIELD volta ao FIELD. Sem checkUserAuth
        // aqui: ele conectaria o realtime do desktop. O FIELD revalida a
        // sessao em /api/auth/me ao abrir; carga completa (replace) para nao
        // levar estado do desktop e para o Android reabrir o escopo do app.
        if (returnTo) {
            setLoadingStage('SESSION VERIFIED');
            playProceduralSound('success');
            window.location.replace(returnTo);
            return;
        }

        // Força o AuthContext a buscar a identidade autoritativa do backend e captura o resultado
        const updatedUser = await checkUserAuth(authenticatedUserPayload, authenticatedSessionPayload);

        // O rtClient agora será conectado pelo AuthContext quando o usuário for validado.

        // small success pause
        setLoadingStage('SESSION VERIFIED');
        playProceduralSound('success');
        await new Promise((r) => setTimeout(r, 600));
        setLoadingStage(null);

        // A lógica de roteamento agora usa o usuário retornado, que é a fonte da verdade.
        const userRole = updatedUser?.role;
        const isPrivileged = ['webmaster', 'institutional_owner', 'super_admin', 'simulation_manager'].includes(userRole);
        const hasInvite = Boolean(inviteRoomId);

        let postAuthTarget = 'roomSelector';

        if (hasInvite) {
            // GUEST WITH INVITE CODE: Go directly to StagingRoom for that specific room
            console.log('[AccessGateway] Guest with invite - routing to StagingRoom');
            try { makeTrace({ eventType: 'INVITE_ACCESS', operatorId: updatedUser?.name, role: userRole, sourceModule: 'AccessGateway', details: { inviteRoomId } }); } catch (e) { }
            postAuthTarget = 'stagingRoom';
        } else if (isPrivileged) {
            // PRIVILEGED USER: Show RoomSelector to choose/create rooms
            console.log('[AccessGateway] Privileged user - routing to RoomSelector');
            try { makeTrace({ eventType: 'PRIVILEGED_ACCESS', operatorId: updatedUser?.name, role: userRole, sourceModule: 'AccessGateway' }); } catch (e) { }
            postAuthTarget = 'roomSelector';
        } else {
            // REGULAR USER WITHOUT INVITE: Show RoomSelector to see available rooms
            console.log('[AccessGateway] Regular user - routing to RoomSelector');
            try { makeTrace({ eventType: 'REGULAR_ACCESS', operatorId: updatedUser?.name, role: userRole, sourceModule: 'AccessGateway' }); } catch (e) { }
            postAuthTarget = 'roomSelector';
        }

        onClose && onClose();
        const handledByCaller = onAuthSuccess ? onAuthSuccess({ target: postAuthTarget }) : false;
        if (!handledByCaller) {
            requestPostAuthSplash(postAuthTarget);
        }
    };

    // SEC-INV-01: recuperacao por e-mail. A resposta e sempre a mesma,
    // exista ou nao cadastro (o servidor nao revela).
    const handlePasswordRecovery = async () => {
        const email = recoveryEmail.trim();
        if (!email) {
            setRecoveryStatus({ tone: 'text-amber-300', message: 'Informe o e-mail cadastrado.' });
            return;
        }
        if (recoveryPending) return;
        setRecoveryPending(true);
        setRecoveryStatus({ tone: 'text-cyan-300', message: 'Enviando pedido...' });
        try {
            const resp = await fetch('/api/auth/password-recovery', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code: recoveryCode.trim() || undefined })
            });
            const payload = await resp.json().catch(() => ({}));
            if (!resp.ok || !payload.ok) {
                playProceduralSound('error');
                setRecoveryStatus({ tone: 'text-rose-300', message: payload.details || 'Não foi possível registrar o pedido agora.' });
                return;
            }
            playProceduralSound('success');
            setRecoveryStatus({ tone: 'text-emerald-300', message: payload.message });
        } catch (error) {
            playProceduralSound('error');
            setRecoveryStatus({ tone: 'text-rose-300', message: 'Servidor indisponível. Tente novamente.' });
        } finally {
            setRecoveryPending(false);
        }
    };

    const handleCredentialSubmit = async (e) => {
        e && e.preventDefault();
        if (credentialPending) return;
        if (newPassword.length < 8) {
            setCredentialStatus({ tone: 'text-amber-300', message: 'A senha precisa ter ao menos 8 caracteres.' });
            return;
        }
        if (newPassword !== newPasswordConfirm) {
            setCredentialStatus({ tone: 'text-amber-300', message: 'As duas senhas não conferem.' });
            return;
        }
        setCredentialPending(true);
        setCredentialStatus({ tone: 'text-cyan-300', message: 'Salvando senha...' });
        try {
            const resp = await fetch('/api/auth/password-reset', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: credentialToken, newPassword })
            });
            const payload = await resp.json().catch(() => ({}));
            if (!resp.ok || !payload.ok) {
                playProceduralSound('error');
                setCredentialStatus({ tone: 'text-rose-300', message: payload.details || 'Não foi possível salvar a senha.' });
                return;
            }
            playProceduralSound('success');
            setNewPassword('');
            setNewPasswordConfirm('');
            setCredentialStatus({
                tone: 'text-emerald-300',
                done: true,
                guest: Boolean(payload.inviteCredential),
                message: payload.inviteCredential
                    ? 'Senha definida. Agora entre em "Convidado" com o código do convite e a nova senha.'
                    : 'Senha redefinida. Entre com o seu e-mail e a nova senha.'
            });
        } catch (error) {
            playProceduralSound('error');
            setCredentialStatus({ tone: 'text-rose-300', message: 'Servidor indisponível. Tente novamente.' });
        } finally {
            setCredentialPending(false);
        }
    };

    const recoveryPanel = (
        <div className="mt-3 rounded border border-cyan-500/20 bg-black/30 p-3">
            <button type="button" aria-expanded={recoveryOpen} onClick={() => setRecoveryOpen((open) => !open)} className="min-h-11 text-xs font-bold uppercase tracking-[0.12em] text-amber-200 hover:text-amber-100 sm:min-h-0">
                {recoveryOpen ? 'Fechar recuperação de senha' : 'Esqueci a senha'}
            </button>
            {recoveryOpen && (
                <div className="mt-2">
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <input
                            value={recoveryEmail}
                            onChange={(event) => setRecoveryEmail(event.target.value)}
                            type="email"
                            autoComplete="email"
                            placeholder="E-mail cadastrado"
                            className="min-w-0 flex-1 rounded border border-cyan-700 bg-black/50 px-3 py-2 text-xs text-cyan-100"
                        />
                        <input
                            value={recoveryCode}
                            onChange={(event) => setRecoveryCode(event.target.value)}
                            placeholder="Código do convite (opcional)"
                            className="min-w-0 flex-1 rounded border border-cyan-700 bg-black/50 px-3 py-2 font-mono text-xs text-cyan-100"
                        />
                        <button type="button" disabled={recoveryPending} onMouseEnter={() => playProceduralSound('button')} onClick={handlePasswordRecovery} className="rounded border border-amber-500/60 px-3 py-2 text-xs font-bold uppercase text-amber-200 hover:bg-amber-500/10 disabled:opacity-50">
                            Enviar link
                        </button>
                    </div>
                    <p className="mt-2 text-[10px] text-gray-500">Enviamos um link de uso único, válido por 30 minutos. O código do convite só escolhe qual convite redefinir. Contas nativas ATAC: peça ao Webmaster.</p>
                    {recoveryStatus && <p role="status" className={`mt-2 text-xs ${recoveryStatus.tone}`}>{recoveryStatus.message}</p>}
                </div>
            )}
        </div>
    );

    return createPortal(
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    // FIELD-P3 (celular): o cartao e mais alto que a tela; com
                    // items-center num contêiner fixo o topo ficava cortado e
                    // nao havia rolagem. overflow-y-auto + my-auto centraliza
                    // quando cabe e rola quando nao cabe (teclado virtual).
                    className="fixed inset-0 z-50 flex justify-center overflow-y-auto px-3 py-4 sm:px-0 sm:py-0"
                >
                    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm scan-overlay" onClick={onClose}></div>
                    <GlassCard className="relative z-10 my-auto w-full max-w-3xl p-4 sm:p-6" size="lg">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-2xl font-bold text-cyan-200 tracking-wide">PORTAL DE ACESSO</h2>
                                <div className="text-xs text-amber-300 uppercase tracking-[0.14em]">Access Gateway</div>
                                <div className="text-sm text-amber-300 uppercase tracking-[0.16em]">Terminal Operacional de Acesso</div>
                                <p className="mt-2 text-xs text-gray-300 max-w-xl">Identifique-se para acessar o núcleo operacional. Use credenciais institucionais, token de missão ou código de convidado.</p>
                            </div>
                            <div className="hidden text-right sm:block">
                                <div className="text-xs text-gray-400">Session</div>
                                <div className="text-sm font-mono text-cyan-200">{new Date().toLocaleString()}</div>
                            </div>
                        </div>

                        <div className="mt-4">
                            {mode !== 'credential' && <div className="flex flex-wrap gap-2 bg-black/20 p-1 rounded-md">
                                <button type="button" onMouseEnter={() => playProceduralSound('button')} onClick={() => setMode('operator')} className={`min-h-11 px-3 py-1 rounded sm:min-h-0 ${mode === 'operator' ? 'bg-cyan-700 text-white' : 'text-cyan-200'}`}>Conta</button>
                                <button type="button" onMouseEnter={() => playProceduralSound('button')} onClick={() => setMode('guest')} className={`min-h-11 px-3 py-1 rounded sm:min-h-0 ${mode === 'guest' ? 'bg-amber-700 text-white' : 'text-amber-200'}`}>Convidado</button>
                                <button type="button" onMouseEnter={() => playProceduralSound('button')} onClick={() => setMode('training')} className={`min-h-11 px-3 py-1 rounded sm:min-h-0 ${mode === 'training' ? 'bg-fuchsia-700 text-white' : 'text-fuchsia-200'}`}>Treinamento</button>
                            </div>}

                            {mode === 'credential' && (
                                <form onSubmit={handleCredentialSubmit} className="grid grid-cols-1 gap-3 mt-4">
                                    <div>
                                        <label htmlFor="credential-new-password" className="text-xs text-gray-200 uppercase">DEFINIR SENHA</label>
                                        <div className="text-[10px] text-gray-400 mb-1">Senha pessoal, com ao menos 8 caracteres. Ninguém mais a conhece.</div>
                                        <PasswordInput wrapperClassName="mt-1" id="credential-new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" className="w-full p-3 bg-black/40 border border-cyan-600 rounded font-mono text-cyan-100" />
                                        <label htmlFor="credential-confirm-password" className="text-xs text-gray-200 mt-2 uppercase block">CONFIRMAR SENHA</label>
                                        <PasswordInput wrapperClassName="mt-1" id="credential-confirm-password" value={newPasswordConfirm} onChange={(event) => setNewPasswordConfirm(event.target.value)} autoComplete="new-password" className="w-full p-3 bg-black/40 border border-cyan-600 rounded font-mono text-cyan-100" />
                                        <p className="text-xs text-gray-500 mt-2">O link vale uma única vez. Definir a senha encerra sessões abertas desta credencial; instituição, operação e permissões não mudam.</p>
                                    </div>
                                    {credentialStatus && <p role="status" className={`text-sm ${credentialStatus.tone}`}>{credentialStatus.message}</p>}
                                    <div className="flex gap-3 mt-1">
                                        {credentialStatus?.done ? (
                                            <button type="button" onClick={() => { setCredentialStatus(null); setMode(credentialStatus.guest ? 'guest' : 'operator'); }} className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-cyan-700 text-black font-bold rounded">IR PARA O LOGIN</button>
                                        ) : (
                                            <button type="submit" disabled={credentialPending || !credentialToken} className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-cyan-700 text-black font-bold rounded disabled:opacity-50">SALVAR SENHA</button>
                                        )}
                                        <button type="button" onClick={onClose} className="px-4 py-2 bg-black border border-gray-600 text-gray-300 rounded hover:bg-gray-800 transition-colors">Cancelar</button>
                                    </div>
                                </form>
                            )}

                            {mode !== 'credential' && <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-3 mt-4">
                                {mode === 'operator' && (
                                    <div>
                                        <label className="text-xs text-gray-200 uppercase">IDENTIFICAÇÃO OPERACIONAL</label>
                                        <div className="text-[10px] text-gray-400 mb-1">E-mail ou identificador</div>
                                        <input name="operatorId" placeholder="seu@email.com.br" value={form.operatorId} onChange={handleChange} className="w-full mt-1 p-3 bg-black/40 border border-cyan-600 rounded font-mono text-cyan-100" />

                                        <label className="text-xs text-gray-200 mt-2 uppercase">CHAVE DE ACESSO</label>
                                        <div className="text-[10px] text-gray-400 mb-1">Access Key</div>
                                        <div className="relative mt-1">
                                            <input name="accessKey" placeholder="••••••••••" value={form.accessKey} onChange={handleChange} type={showPassword ? "text" : "password"} className="w-full p-3 pr-12 bg-black/40 border border-cyan-600 rounded font-mono text-cyan-100" />
                                            <button
                                                type="button"
                                                aria-label={showPassword ? 'Ocultar senha' : 'Visualizar senha'}
                                                onMouseDown={(event) => event.preventDefault()}
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded border border-cyan-500/20 text-cyan-200 transition-colors hover:bg-cyan-500/10 hover:text-cyan-100"
                                            >
                                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">Professores, operadores e gestores: entre com o e-mail da sua conta. O papel é reconhecido automaticamente.</p>
                                        <p className="text-[10px] text-gray-500 mt-1">Recebeu um convite? Use a aba Convidado, com o código do convite e a senha.</p>
                                        {recoveryPanel}
                                    </div>
                                )}

                                {mode === 'guest' && (
                                    <div>
                                        <label htmlFor="guest-code" className="text-xs text-gray-200 uppercase">CÓDIGO DO CONVITE</label>
                                        <div className="text-[10px] text-gray-400 mb-1">Recebido por e-mail ou entregue pelo gestor</div>
                                        <input id="guest-code" name="guestCode" autoComplete="username" placeholder="Ex: GUEST-AB12CD-EF34GH" value={form.guestCode} onChange={handleChange} className="w-full mt-1 p-3 bg-black/30 border border-amber-500/30 rounded font-mono text-amber-100" />

                                        <label htmlFor="guest-password" className="text-xs text-gray-200 mt-2 uppercase">SENHA</label>
                                        <div className="text-[10px] text-gray-400 mb-1">A sua senha, ou a senha temporária entregue pelo gestor</div>
                                        <div className="relative mt-1">
                                            <input id="guest-password" name="missionToken" autoComplete="current-password" placeholder="••••••••••" value={form.missionToken} onChange={handleChange} type={showPassword ? "text" : "password"} className="w-full p-3 pr-12 bg-black/30 border border-amber-500/30 rounded font-mono text-amber-100" />
                                            <button
                                                type="button"
                                                aria-label={showPassword ? 'Ocultar senha' : 'Visualizar senha'}
                                                onMouseDown={(event) => event.preventDefault()}
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded border border-amber-500/20 text-amber-200 transition-colors hover:bg-amber-500/10 hover:text-amber-100"
                                            >
                                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">Acesso restrito à operação do convite. Recebeu o convite por e-mail? Defina a senha pelo link da mensagem antes de entrar. Senha temporária deve ser trocada no primeiro acesso. Depois de entrar, você pode aguardar a admissão pelo comando da operação.</p>
                                        {recoveryPanel}
                                    </div>
                                )}

                                {mode === 'training' && (
                                    <div>
                                        <label className="text-xs text-gray-200 uppercase">CHAVE DE TREINAMENTO</label>
                                        <div className="text-[10px] text-gray-400 mb-1">Training Access Key</div>
                                        <input name="trainingKey" placeholder="Ex: TRAINING-DELTA-2026" value={form.trainingKey} onChange={handleChange} className="w-full mt-1 p-3 bg-black/30 border border-fuchsia-500/30 rounded font-mono text-fuchsia-100" />
                                        <p className="text-xs text-gray-500 mt-2">Modo acadêmico: simulações controladas.</p>
                                    </div>
                                )}

                                {error && <div className="text-rose-400 text-sm">{error}</div>}

                                <div className="flex gap-3 mt-3">
                                    <button type="submit" onMouseEnter={() => playProceduralSound('button')} className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-cyan-700 text-black font-bold rounded hover:from-cyan-500 hover:to-cyan-600 transition-all">{mode === 'operator' ? 'ENTRAR NA OPERAÇÃO' : mode === 'guest' ? 'ENTRAR NA SESSÃO' : 'ACESSAR TREINAMENTO'}</button>
                                    <button type="button" onMouseEnter={() => playProceduralSound('button')} onClick={onClose} className="px-4 py-2 bg-black border border-gray-600 text-gray-300 rounded hover:bg-gray-800 transition-colors">Cancelar</button>
                                </div>
                            </form>}
                        </div>

                        <div className="mt-4 pt-4 border-t border-white/5">
                            <div className="flex flex-wrap justify-between items-center gap-2 text-xs text-gray-300">
                                <div className="flex flex-wrap gap-x-6 gap-y-1">
                                    <div>CORE STATUS <span className="text-amber-300 ml-2">STANDBY</span></div>
                                    <div>EVENT BUS <span className="text-cyan-300 ml-2">READY</span></div>
                                    <div>AI NODES <span className="text-emerald-300 ml-2">ONLINE</span></div>
                                    <div>TACTICAL GRID <span className="text-lime-300 ml-2">LINKED</span></div>
                                </div>
                                <div className="font-mono text-xs text-gray-400">{loadingStage || 'SYSTEM IDLE'}</div>
                            </div>
                            {/* subtle scanline / glow */}
                            <div className="mt-2 h-1 w-full bg-gradient-to-r from-transparent via-cyan-400/10 to-transparent rounded" style={{ opacity: Math.abs(Math.sin(statusAnim)) }} />
                        </div>
                    </GlassCard>
                </motion.div>
            )}
        </AnimatePresence>, document.body
    );
}

// mount room selector and staging room portals at root level
export function RoomSelectorMount() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const t = setInterval(() => {
            if (window.__openRoomSelector) {
                setOpen(true);
                window.__openRoomSelector = false;
            }
        }, 300);
        return () => clearInterval(t);
    }, []);

    const handleRoomSelected = (room) => {
        setOpen(false);
        // After room selection, open staging room
        window.__openStagingRoom = true;
    };

    return <RoomSelector isOpen={open} onClose={() => setOpen(false)} onRoomSelected={handleRoomSelected} />;
}

export function StagingRoomMount() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const t = setInterval(() => {
            if (window.__openStagingRoom) {
                setOpen(true);
                window.__openStagingRoom = false;
            }
        }, 300);
        return () => clearInterval(t);
    }, []);

    return <StagingRoom isOpen={open} onClose={() => setOpen(false)} />;
}
